import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container as MantineContainer, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  buildPatchDto,
  Container,
  CONTAINER_PATCH_FIELD_MAP,
  ContainerPatchDTO,
  containerSelector,
  getObjectId,
  useGetContainerQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {
  ContainerForm,
  ContainerFormDefaultValues,
  ContainerFormValues
} from "../../components/form/ContainerForm/ContainerForm";
import {useContainerMutation} from "../../hooks/useContainerMutation";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";
import {v4 as uuid} from "uuid";
import {toFormDefaultsFromContainer} from "../../components/form/ContainerForm/useContainerForm";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: ContainerFormDefaultValues
}

export const ContainerFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return (
    <ContainerFormPage
      id={id}
      copy={copy}
      formDefaults={formDefaults}
    />
  );
};

export const ContainerFormPage = (props: Props) => {
  const {
    id,
    copy = false,
    formDefaults,
  } = props;

  const {regionId} = useApp()

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const [access, setAccess] = useState(false);

  const selectContainer = useMemo(() => {
    return containerSelector()
  }, [])

  const {currContainer, isLoading} = useGetContainerQuery(access ? (id ?? skipToken) : skipToken, {
    selectFromResult: (result) => ({
      currContainer: selectContainer(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: ContainerFormDefaultValues = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromContainer(currContainer);
  }, [currContainer, formDefaults]);

  const isEdit = useMemo(() => {
    return currContainer && !copy
  }, [currContainer, copy])

  const title: string = useMemo(() => {
    if (isEdit) {
      return t("page.entity.container.form.title.edit", "Edit Container", {ns: "common"});
    } else {
      return t("page.entity.container.form.title.new", "New Container", {ns: "common"});
    }
  }, [isEdit, t]);

  const {
    createContainer,
    patchUpdateContainer,
    isCreating,
    isUpdating,
  } = useContainerMutation()

  const onContainerSubmit = useCallback((formValues: ContainerFormValues) => {
    const patch: Partial<Container> = {
      externalId: formValues.name,
      abbr: formValues.abbr,
      barCode: formValues.barcode,
      leaseId: formValues.leaseId,
      leaseName: formValues.leaseName,
      afe: formValues.afe,
      afePrefix: formValues.afePrefix,
      afeSuffix: formValues.afeSuffix,
      type: formValues.type?.code,
      owningCompanyId: getObjectId(formValues.company),
      waterTypeId: getObjectId(formValues.freightType),
      siteId: getObjectId(formValues.site),
      containerClusterId: getObjectId(formValues.cluster),
      sizeDecimal: formValues.size.number,
      sizeUnitOfMeasure: formValues.size.units,
      weight: formValues.weight?.number,
      weightUnitOfMeasure: formValues.weight?.units,
      height: formValues.dimensions?.height,
      diameter: formValues.dimensions?.diameter,
      diamterUnitOfMeasure: formValues.dimensions?.units,
      scadaMode: formValues.scadaMode?.code,
      scadaModeReason: formValues.scadaModeReason,
      dispatchIgnoreMaxNonPrimaryVolume: formValues.dispatchIgnoreMaxNonPrimaryVolume,
      doNotDispatch: formValues.doNotDispatch,
      doNotUse: formValues.doNotUse,
      active: formValues.active,
    }

    if (isEdit) {
      const dto: ContainerPatchDTO = buildPatchDto(patch, CONTAINER_PATCH_FIELD_MAP)
      patchUpdateContainer(currContainer.id, dto, {onSuccess: () => navigate(-1)})
    } else {
      const container: Partial<Container> = {
        ...patch,
        id: uuid(), // TODO: remove once api returns container payload
      };
      createContainer(container, {onSuccess: (payload) => navigate(`/containers/profile?id=${container.id}`, {replace: true})})
    }
  }, [currContainer, isEdit, createContainer, patchUpdateContainer, navigate]);

  return (
    <ProtectedPage
      entity={currContainer}
      permissions={[isEdit ? "container_edit" : "container_create"]}
      isLoading={isLoading}
      onAccess={setAccess}>
      {(classes) => (
        <MantineContainer className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {title}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <ContainerForm
                  options={{
                    regionId: regionId,
                  }}
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onContainerSubmit}/>
              </Paper>
            )}
          </Center>
        </MantineContainer>
      )}
    </ProtectedPage>
  );
};

